import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import {of as observableOf,  Observable } from 'rxjs';
import {catchError} from 'rxjs/operators';
import { ErrorResponse, SuccessResponse } from '@shared/services/api/response';
import { IIntegrationKey, IIntegrationCode, IIntegrationValidation, IIntegrationAuthorization, IConnectorValidations,
     IZoomWebinars, IPublicEventSsoGetItem, IPublicEventSsoUpsertRequest, IPublicEventSsoValidateItem } from './integrations-model';

@Injectable()
export class IntegrationsService {
    constructor(
        private http: HttpClient
    ) { }

    private static get URL(): string { return `${environment.dashboardApi.baseUrl}${environment.dashboardApi.prefix}`; }
    private static readonly ENDPOINT_CONNECTOR = 'crms';
    private static readonly ENDPOINT_STRIPE = 'stripes';
    private static readonly ENDPOINT_LINEUPR = 'lineuprs';
    private static readonly ENDPOINT_HALITO = 'halito';
    private static readonly ENDPOINT_ZOOM = 'zooms';
    private static readonly ENDPOINT_FIELDDRIVE = 'fielddrives';
    private static readonly ENDPOINT_PUBLIC_EVENT_SSO = 'integrations/publiceventssos';

    /** GET */
    public getConnectorValidation(): Observable<SuccessResponse<IConnectorValidations> | ErrorResponse>  {
        return this.http
            .get<SuccessResponse<IConnectorValidations>>(
                IntegrationsService.URL +
                IntegrationsService.ENDPOINT_CONNECTOR +
                '/' + 'tenants'
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    public getStripeValidation(): Observable<SuccessResponse<IIntegrationValidation> | ErrorResponse>  {
        return this.http
            .get<SuccessResponse<IIntegrationValidation>>(
                IntegrationsService.URL +
                IntegrationsService.ENDPOINT_STRIPE +
                '/' + 'integrations' + '/' + 'validations'
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    public getLineUprValidation(): Observable<SuccessResponse<IIntegrationValidation> | ErrorResponse>  {
        return this.http
            .get<SuccessResponse<IIntegrationValidation>>(
                IntegrationsService.URL +
                IntegrationsService.ENDPOINT_LINEUPR +
                '/' + 'integrations' + '/' + 'validations'
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    public getHalitoValidation(): Observable<SuccessResponse<IIntegrationValidation> | ErrorResponse>  {
        return this.http
            .get<SuccessResponse<IIntegrationValidation>>(
                IntegrationsService.URL +
                IntegrationsService.ENDPOINT_HALITO +
                '/' + 'integrations' + '/' + 'validations'
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    public getZoomValidation(): Observable<SuccessResponse<IIntegrationValidation> | ErrorResponse>  {
        return this.http
            .get<SuccessResponse<IIntegrationValidation>>(
                IntegrationsService.URL +
                IntegrationsService.ENDPOINT_ZOOM +
                '/' + 'integrations' + '/' + 'validations'
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    public getZoomAuthorization(): Observable<SuccessResponse<IIntegrationAuthorization> | ErrorResponse>  {
        return this.http
            .get<SuccessResponse<IIntegrationAuthorization>>(
                IntegrationsService.URL +
                IntegrationsService.ENDPOINT_ZOOM +
                '/' + 'authorizationurls'
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    public getZoomWebinars(): Observable<SuccessResponse<IZoomWebinars> | ErrorResponse>  {
        return this.http
            .get<SuccessResponse<IZoomWebinars>>(
                IntegrationsService.URL +
                IntegrationsService.ENDPOINT_ZOOM +
                '/' + 'webinars'
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }


    /** POST */
    public addStripeKey(apiKey: IIntegrationKey): Observable<SuccessResponse<boolean> | ErrorResponse>  {
        return this.http
            .post<SuccessResponse<boolean>>(
                IntegrationsService.URL +
                IntegrationsService.ENDPOINT_STRIPE +
                '/' + 'integrations',
                apiKey
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    public addZoomToken(code: IIntegrationCode): Observable<SuccessResponse<boolean> | ErrorResponse>  {
        return this.http
            .post<SuccessResponse<boolean>>(
                IntegrationsService.URL +
                IntegrationsService.ENDPOINT_ZOOM +
                '/' + 'integrations',
                code
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }


    /** DELETE */
    public deleteStripeKey(): Observable<SuccessResponse<boolean> | ErrorResponse>  {
        return this.http
            .delete<SuccessResponse<boolean>>(
                IntegrationsService.URL +
                IntegrationsService.ENDPOINT_STRIPE +
                '/' + 'integrations',
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    public deleteFieldDrive(): Observable<SuccessResponse<boolean> | ErrorResponse>  {
        return this.http
            .delete<SuccessResponse<boolean>>(
                IntegrationsService.URL +
                IntegrationsService.ENDPOINT_FIELDDRIVE +
                '/' + 'integrations',
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    public deleteZoomToken(): Observable<SuccessResponse<boolean> | ErrorResponse>  {
        return this.http
            .delete<SuccessResponse<boolean>>(
                IntegrationsService.URL +
                IntegrationsService.ENDPOINT_ZOOM +
                '/' + 'integrations',
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    public getFieldDriveValidation(): Observable<SuccessResponse<IIntegrationValidation> | ErrorResponse>  {
        return this.http
            .get<SuccessResponse<IIntegrationValidation>>(
                IntegrationsService.URL +
                IntegrationsService.ENDPOINT_FIELDDRIVE +
                '/' + 'integrations' + '/' + 'validations'
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

     /** POST */
     public addFieldDriveKey(apiKey: IIntegrationKey): Observable<SuccessResponse<boolean> | ErrorResponse>  {
        return this.http
            .post<SuccessResponse<boolean>>(
                IntegrationsService.URL +
                IntegrationsService.ENDPOINT_FIELDDRIVE +
                '/' + 'integrations',
                apiKey
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    /** Public Event SSO - GET (data can be null when not configured) */
    public getPublicEventSso(): Observable<SuccessResponse<IPublicEventSsoGetItem> | ErrorResponse>  {
    return this.http
        .get<SuccessResponse<IPublicEventSsoGetItem>>(
        IntegrationsService.URL + IntegrationsService.ENDPOINT_PUBLIC_EVENT_SSO
        )
        .pipe(catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    /** Public Event SSO - POST (Add) */
    public addPublicEventSso(request: IPublicEventSsoUpsertRequest): Observable<SuccessResponse<boolean> | ErrorResponse>  {
    return this.http
        .post<SuccessResponse<boolean>>(
        IntegrationsService.URL + IntegrationsService.ENDPOINT_PUBLIC_EVENT_SSO,
        request
        )
        .pipe(catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    /** Public Event SSO - PUT (Update) */
    public updatePublicEventSso(request: IPublicEventSsoUpsertRequest): Observable<SuccessResponse<boolean> | ErrorResponse>  {
    return this.http
        .put<SuccessResponse<boolean>>(
        IntegrationsService.URL + IntegrationsService.ENDPOINT_PUBLIC_EVENT_SSO,
        request
        )
        .pipe(catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    /** Public Event SSO - DELETE */
    public deletePublicEventSso(): Observable<SuccessResponse<boolean> | ErrorResponse>  {
    return this.http
        .delete<SuccessResponse<boolean>>(
        IntegrationsService.URL + IntegrationsService.ENDPOINT_PUBLIC_EVENT_SSO
        )
        .pipe(catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    /** Public Event SSO - VALIDATE */
    public validatePublicEventSso(): Observable<SuccessResponse<IPublicEventSsoValidateItem> | ErrorResponse>  {
    return this.http
        .get<SuccessResponse<IPublicEventSsoValidateItem>>(
        IntegrationsService.URL + IntegrationsService.ENDPOINT_PUBLIC_EVENT_SSO + '/validations'
        )
        .pipe(catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

}
